/**
 * Simulador v2 (#36+#37 PR-B) — cronograma de TRANCHES + volume MWm + toggle
 * de LENTES (Nominal · IPCA até o fim · VP). Spec: docs/mocks/simulador_v2_mock.html.
 *
 * O front manda SEMPRE `tranches[]` (1 tranche = caso simples) pro
 * POST /api/simulador v2, debounced ~400ms, nonce INCONDICIONAL (fix F1 do
 * v1: transição válido→inválido também invalida response em voo). Nada é
 * gravado (endpoint stateless; selo visível).
 *
 * Decisões:
 * - Contraparte segue PICKER r3 (linhas do /api/exposicao; "nova" só por
 *   clique explícito). F3: dropdown fecha em clique-fora/Esc. F4: se o
 *   /api/exposicao falhar, "nova contraparte" SEGUE acessível (degrada).
 * - Toggle de lente re-expressa TOTAIS e CADA LINHA das tabelas de MtM e
 *   Fluxo; lente indisponível = botão desabilitado com motivo (nominal
 *   sempre vivo; lente ativa que ficar indisponível cai pra nominal).
 * - Total de lente None (<100% coberto) → "—" + nota n_sem_* (nunca parcial
 *   mudo — o backend garante, o front expõe).
 * - Exposição: lente NÃO se aplica (posição/limite são nominais) — card fica
 *   em nominal com nota. Limite gated #9 (texto, ZERO botão — #246).
 * - Validação espelha os 400 POR TRANCHE ("tranche N: …") + cap total 120.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { brDate } from "../../format";
import { fmtBRL, fmtBRLcompact, fmtBRLcompactSigned } from "../../utils/formatters";
import { getExposicao, postSimuladorV2 } from "../../api/client";
import type {
  SimuladorV2Request,
  SimuladorV2Response,
  SimuladorV2Tranche,
} from "../../api/types";

const DEBOUNCE_MS = 400;
const MAX_MESES_TOTAL = 120; // trava do backend (soma de tranche-meses)

type Indexador = SimuladorV2Request["indexador"];
const INDEXADORES: Indexador[] = ["fixo", "%PLD", "PLD+", "COLLAR", "IPCA"];
const INDEXADO = new Set<Indexador>(["%PLD", "PLD+", "COLLAR"]);

type Lente = "nominal" | "ipca_fv" | "vp";

interface Cabecalho {
  tipo: "compra" | "venda";
  submercado: "SE" | "S" | "NE" | "N";
  tipoEnergia: "CONV" | "I0" | "I5" | "I100";
  indexador: Indexador;
}

/** Linha da tabela de tranches (inputs crus; id estável pro React). */
interface TrancheForm {
  id: number;
  mesIni: string; // YYYY-MM
  mesFim: string;
  volumeMwm: string;
  preco: string;
  spread: string;
  ipcaBase: string; // YYYY-MM-DD
  ipcaReaj: string;
}

let _seq = 0;
const novaTranche = (): TrancheForm => ({
  id: ++_seq, mesIni: "", mesFim: "", volumeMwm: "", preco: "", spread: "",
  ipcaBase: "", ipcaReaj: "",
});

interface CpEscolhida {
  nome: string;
  nova: boolean;
}

function mesesIdx(ym: string): number | null {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return null;
  return Number(m[1]) * 12 + Number(m[2]);
}

/** nº de meses de uma tranche válida (fim>=ini); null se período incompleto. */
function nMeses(t: TrancheForm): number | null {
  const a = mesesIdx(t.mesIni);
  const b = mesesIdx(t.mesFim);
  if (a === null || b === null || b < a) return null;
  return b - a + 1;
}

/** Espelho client-side dos 400 do v2 (`normalizar_tranches`): pendências POR
 * TRANCHE prefixadas + cabeçalho + cap total. Vazio = pode submeter. */
export function pendenciasV2(
  cab: Cabecalho, cp: CpEscolhida | null, tranches: TrancheForm[],
): string[] {
  const out: string[] = [];
  if (!cp) out.push("contraparte (escolha da lista ou marque como nova)");
  let totalMeses = 0;
  tranches.forEach((t, i) => {
    const n = i + 1;
    const vm = Number(t.volumeMwm);
    if (!t.volumeMwm.trim() || !Number.isFinite(vm) || vm <= 0) {
      out.push(`tranche ${n}: volume > 0 (MWm)`);
    }
    if (INDEXADO.has(cab.indexador)) {
      const s = Number(t.spread);
      if (!t.spread.trim() || !Number.isFinite(s)) {
        out.push(`tranche ${n}: spread em R$/MWh (${cab.indexador})`);
      }
    } else {
      const p = Number(t.preco);
      if (!t.preco.trim() || !Number.isFinite(p) || p <= 0) {
        out.push(`tranche ${n}: preço > 0 em R$/MWh`);
      }
    }
    if (cab.indexador === "IPCA" && (!t.ipcaBase || !t.ipcaReaj)) {
      out.push(`tranche ${n}: IPCA exige data base e data de reajuste`);
    }
    const meses = nMeses(t);
    if (meses === null) {
      const a = mesesIdx(t.mesIni);
      const b = mesesIdx(t.mesFim);
      if (a !== null && b !== null && b < a) {
        out.push(`tranche ${n}: mês fim antes do início`);
      } else {
        out.push(`tranche ${n}: período (mês início e fim)`);
      }
    } else {
      totalMeses += meses;
    }
  });
  if (totalMeses > MAX_MESES_TOTAL) {
    out.push(`período total das tranches > ${MAX_MESES_TOTAL} meses (soma=${totalMeses})`);
  }
  return out;
}

function montarBody(
  cab: Cabecalho, cp: CpEscolhida, tranches: TrancheForm[],
): SimuladorV2Request {
  const idx = cab.indexador;
  const trs: SimuladorV2Tranche[] = tranches.map((t) => {
    const tr: SimuladorV2Tranche = {
      mes_inicio: t.mesIni, mes_fim: t.mesFim, volume_mwm: Number(t.volumeMwm),
    };
    if (INDEXADO.has(idx)) tr.spread = Number(t.spread);
    else tr.preco = Number(t.preco);
    if (idx === "IPCA") {
      tr.ipca_data_base = t.ipcaBase;
      tr.ipca_data_reajuste = t.ipcaReaj;
    }
    return tr;
  });
  return {
    tipo: cab.tipo, submercado: cab.submercado, tipo_energia: cab.tipoEnergia,
    contraparte: cp.nome, indexador: idx, tranches: trs,
  };
}

// ---- UI helpers (skin navy+teal da casa) --------------------------------------

const INPUT_CLS =
  "bg-indra-azul-deep border border-white/10 rounded-md px-3 py-1.5 text-sm text-white " +
  "placeholder-white/30 focus:outline-none focus:border-indra-turquesa/60 w-full";

function corValor(v: number): string {
  if (Math.abs(v) < 0.005) return "text-white/40";
  return v >= 0 ? "text-indra-positivo" : "text-indra-negativo";
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-white/50 font-medium">{label}</span>
      {children}
    </label>
  );
}

function Toggle<T extends string>({
  opts, value, onChange, ariaLabel,
}: {
  opts: { id: T; label: string; disabled?: boolean; hint?: string }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      className="inline-flex rounded-md border border-white/10 bg-indra-azul-deep p-0.5 text-xs"
      role="group"
      aria-label={ariaLabel}
    >
      {opts.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => !o.disabled && onChange(o.id)}
          aria-pressed={value === o.id}
          disabled={o.disabled}
          title={o.hint}
          className={`px-3 py-1.5 rounded transition-colors ${
            value === o.id
              ? "bg-white/15 text-white font-semibold"
              : o.disabled
                ? "text-white/25 cursor-not-allowed"
                : "text-white/50 hover:text-white/80"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const DIR_BADGE: Record<string, string> = {
  comprado: "bg-indra-positivo/12 text-indra-positivo",
  vendido: "bg-indra-negativo/12 text-indra-negativo",
  casado: "bg-amber-400/12 text-amber-400",
};

function DirecaoBadge({ direcao }: { direcao: string | null }) {
  if (!direcao) return <span className="text-white/30 text-[11px] italic">sem posição</span>;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${DIR_BADGE[direcao] ?? "bg-white/10 text-white/60"}`}>
      net {direcao}
    </span>
  );
}

function DeltaCard({
  titulo, asOf, children, testid,
}: {
  titulo: string;
  asOf?: string | null;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <div
      data-testid={testid}
      className="bg-indra-azul-surface border border-white/10 rounded-lg p-4 flex flex-col gap-3"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-bold text-white">{titulo}</h3>
        {asOf && <span className="text-[10px] text-white/40">baseline as-of {brDate(asOf)}</span>}
      </div>
      {children}
    </div>
  );
}

// ---- Picker de contraparte (r3 + F3 clique-fora/Esc + F4 degrada) --------------

function ContrapartePicker({
  nomes, carregando, erro, escolhida, onEscolher,
}: {
  nomes: string[];
  carregando: boolean;
  erro: string | null;
  escolhida: CpEscolhida | null;
  onEscolher: (cp: CpEscolhida | null) => void;
}) {
  const [q, setQ] = useState("");
  const [aberto, setAberto] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // F3: clique-fora fecha o dropdown (mousedown pra fechar antes do focus ir).
  useEffect(() => {
    if (!aberto) return;
    const h = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAberto(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [aberto]);

  const busca = q.trim().toLowerCase();
  const filtradas = useMemo(
    () => (busca ? nomes.filter((n) => n.toLowerCase().includes(busca)) : nomes),
    [nomes, busca],
  );
  const TOP = 40;
  const mostra = filtradas.slice(0, TOP);
  const temExata = busca.length > 0 && nomes.some((n) => n.toLowerCase() === busca);

  function escolher(nome: string, nova: boolean) {
    onEscolher({ nome, nova });
    setQ(nome);
    setAberto(false);
  }

  return (
    <div className="relative" ref={boxRef}>
      <input
        data-testid="sim-cp-input"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setAberto(true);
          onEscolher(null); // digitar desfaz a escolha — nunca texto livre implícito
        }}
        onFocus={() => setAberto(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setAberto(false); // F3
        }}
        placeholder={carregando ? "carregando contrapartes…" : "buscar contraparte…"}
        className={INPUT_CLS}
        autoComplete="off"
      />
      {escolhida && (
        <span
          data-testid="sim-cp-selo"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 rounded bg-indra-turquesa/15 text-indra-turquesa font-semibold"
        >
          {escolhida.nova ? "nova" : "✓"}
        </span>
      )}
      {/* F4: com erro na exposição a lista real fica indisponível, mas o
          caminho EXPLÍCITO de "nova contraparte" segue vivo (degrada, não trava). */}
      {aberto && !carregando && (
        <ul
          data-testid="sim-cp-lista"
          className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-indra-azul-deep border border-white/15 rounded-md shadow-xl text-sm"
        >
          {erro && (
            <li data-testid="sim-cp-erro" className="px-3 py-1.5 text-[11px] text-indra-negativo">
              lista de contrapartes indisponível (exposição falhou) — dá pra seguir com “nova”
            </li>
          )}
          {!erro && mostra.map((n) => (
            <li key={n}>
              <button
                type="button"
                onClick={() => escolher(n, false)}
                className="w-full text-left px-3 py-1.5 text-white/80 hover:bg-white/10 hover:text-white"
              >
                {n}
              </button>
            </li>
          ))}
          {!erro && filtradas.length > TOP && (
            <li className="px-3 py-1.5 text-[11px] text-white/40 italic">
              + {filtradas.length - TOP} outras… refine a busca
            </li>
          )}
          {busca.length > 0 && !temExata && (
            <li className={erro ? "" : "border-t border-white/10"}>
              <button
                type="button"
                data-testid="sim-cp-nova"
                onClick={() => escolher(q.trim(), true)}
                className="w-full text-left px-3 py-1.5 text-indra-turquesa hover:bg-white/10"
              >
                ➕ usar “{q.trim()}” como nova contraparte
              </button>
            </li>
          )}
          {!erro && mostra.length === 0 && busca.length === 0 && (
            <li className="px-3 py-1.5 text-white/40 italic">nenhuma contraparte na exposição</li>
          )}
        </ul>
      )}
    </div>
  );
}

// ---- Mini-timeline das pernas (na lente ativa) ---------------------------------

function PernasTimeline({
  pernas, lente,
}: {
  pernas: SimuladorV2Response["fluxo"]["pernas"];
  lente: Lente;
}) {
  const valorDe = (p: SimuladorV2Response["fluxo"]["pernas"][number]) =>
    lente === "ipca_fv" ? p.valor_ipca_fv : lente === "vp" ? p.valor_vp : p.valor;
  const vals = pernas.map(valorDe).filter((v): v is number => v !== null);
  if (vals.length === 0) return null;
  const maxAbs = Math.max(...vals.map(Math.abs), 0.01);
  const H = 22;
  return (
    // overflow-x (F2): período longo (cap 120m) rola dentro, não sangra o card.
    <div className="overflow-x-auto">
      <div className="flex gap-[3px] items-stretch h-[56px] mt-1" aria-hidden>
        {pernas.map((p) => {
          const v = valorDe(p);
          const h = v === null ? 0 : Math.max(2, (Math.abs(v) / maxAbs) * H);
          const pos = v !== null && v >= 0;
          return (
            <div
              key={p.mes_fornecimento}
              title={`${p.mes_fornecimento} · ${v === null ? "sem valor na lente" : fmtBRL(v)}`}
              className="flex-1 relative min-w-[3px]"
            >
              <div className="absolute left-0 right-0 top-[27px] border-t border-dashed border-white/15" />
              {v === null ? (
                <div className="absolute left-[20%] right-[20%] top-[24px] h-[6px] rounded-[2px] border border-white/25" />
              ) : (
                <div
                  className={`absolute left-[15%] right-[15%] rounded-[2px] ${pos ? "bg-indra-positivo/80" : "bg-indra-negativo/80"}`}
                  style={pos ? { bottom: 29, height: h } : { top: 28, height: h }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Componente principal -------------------------------------------------------

const LENTE_SUB: Record<Lente, string> = {
  nominal: "· nominal",
  ipca_fv: "· em R$ reais (IPCA até o fim)",
  vp: "· a valor presente (curva pré)",
};

export function SimuladorTab() {
  const [cab, setCab] = useState<Cabecalho>({
    tipo: "compra", submercado: "SE", tipoEnergia: "CONV", indexador: "fixo",
  });
  const [cp, setCp] = useState<CpEscolhida | null>(null);
  const [tranches, setTranches] = useState<TrancheForm[]>([novaTranche()]);
  const [lente, setLente] = useState<Lente>("nominal");

  const [cpNomes, setCpNomes] = useState<string[]>([]);
  const [cpCarregando, setCpCarregando] = useState(true);
  const [cpErro, setCpErro] = useState<string | null>(null);

  const [resultado, setResultado] = useState<SimuladorV2Response | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const nonce = useRef(0);

  useEffect(() => {
    let vivo = true;
    getExposicao()
      .then((r) => {
        if (!vivo) return;
        setCpNomes(r.linhas.map((l) => l.contraparte));
        setCpCarregando(false);
      })
      .catch((e) => {
        if (!vivo) return;
        setCpErro(String(e)); // F4: picker degrada, "nova" segue acessível
        setCpCarregando(false);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const faltando = useMemo(() => pendenciasV2(cab, cp, tranches), [cab, cp, tranches]);
  const body = useMemo(
    () => (faltando.length === 0 && cp ? montarBody(cab, cp, tranches) : null),
    [cab, cp, tranches, faltando],
  );
  const bodyKey = body ? JSON.stringify(body) : null;

  // Recompute AO VIVO — nonce INCONDICIONAL (F1): qualquer mudança (inclusive
  // válido→inválido) invalida response em voo; delta velho nunca landa.
  useEffect(() => {
    nonce.current++;
    setResultado(null);
    setErro(null);
    if (!bodyKey) {
      setCalculando(false);
      return;
    }
    setCalculando(true);
    const meu = nonce.current;
    const t = setTimeout(() => {
      postSimuladorV2(JSON.parse(bodyKey) as SimuladorV2Request)
        .then((r) => {
          if (nonce.current !== meu) return;
          setResultado(r);
          setCalculando(false);
        })
        .catch((e) => {
          if (nonce.current !== meu) return;
          setErro(String(e));
          setCalculando(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [bodyKey]);

  // Lente ativa indisponível no payload novo → cai pra nominal (sem número
  // falso). Defensivo F-B1: payload sem `lentes` (drift de contrato) conta
  // como indisponível — nunca derreferencia undefined.
  useEffect(() => {
    if (!resultado || lente === "nominal") return;
    const disp = lente === "ipca_fv"
      ? resultado.lentes?.ipca_fv?.disponivel ?? false
      : resultado.lentes?.vp?.disponivel ?? false;
    if (!disp) setLente("nominal");
  }, [resultado, lente]);

  const setTr = (id: number, patch: Partial<TrancheForm>) =>
    setTranches((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const indexado = INDEXADO.has(cab.indexador);
  const ehIpca = cab.indexador === "IPCA";

  // Rodapé da tabela: meses client-side; MWh/nominal do echo quando disponível.
  const totalMesesForm = tranches.reduce((s, t) => s + (nMeses(t) ?? 0), 0);

  // Valores na lente ativa.
  const mtmDe = (m: SimuladorV2Response["mtm"]["por_mes"][number]) =>
    lente === "ipca_fv" ? m.mtm_ipca_fv : lente === "vp" ? m.mtm_vp : m.mtm;
  const motivoLenteMtm = (m: SimuladorV2Response["mtm"]["por_mes"][number]) =>
    lente === "ipca_fv" ? m.motivo_ipca_fv : lente === "vp" ? m.motivo_vp : null;
  const pernaDe = (p: SimuladorV2Response["fluxo"]["pernas"][number]) =>
    lente === "ipca_fv" ? p.valor_ipca_fv : lente === "vp" ? p.valor_vp : p.valor;
  const motivoLentePerna = (p: SimuladorV2Response["fluxo"]["pernas"][number]) =>
    lente === "ipca_fv" ? p.motivo_ipca_fv : lente === "vp" ? p.motivo_vp : null;

  // `?? null`/`?? 0` (F-B1): payload sem os campos de lente degrada pra
  // "—"/0 em vez de vazar undefined pros formatadores.
  const mtmTotalLente = resultado
    ? lente === "ipca_fv"
      ? resultado.mtm.total_ipca_fv ?? null
      : lente === "vp"
        ? resultado.mtm.total_vp ?? null
        : resultado.mtm.total
    : null;
  const mtmNSem = resultado
    ? lente === "ipca_fv"
      ? resultado.mtm.n_sem_ipca_fv ?? 0
      : lente === "vp"
        ? resultado.mtm.n_sem_vp ?? 0
        : 0
    : 0;
  const fluxoTotalLente = resultado
    ? lente === "ipca_fv"
      ? resultado.fluxo.total_ipca_fv ?? null
      : lente === "vp"
        ? resultado.fluxo.total_vp ?? null
        : null
    : null;

  const lenteOpts = [
    { id: "nominal" as Lente, label: "Nominal" },
    {
      id: "ipca_fv" as Lente,
      label: "IPCA (até o fim)",
      disabled: !!resultado && !(resultado.lentes?.ipca_fv?.disponivel ?? false),
      hint: resultado
        ? (resultado.lentes?.ipca_fv?.disponivel ?? false)
          ? resultado.lentes.ipca_fv.definicao
          : "indisponível: implícita ANBIMA não materializada"
        : undefined,
    },
    {
      id: "vp" as Lente,
      label: "VP",
      disabled: !!resultado && !(resultado.lentes?.vp?.disponivel ?? false),
      hint: resultado
        ? (resultado.lentes?.vp?.disponivel ?? false)
          ? resultado.lentes.vp.definicao
          : "indisponível: curva pré não materializada"
        : undefined,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-white font-sans">Simulador — operação (v2)</h2>
          <p className="text-white/50 text-sm mt-1">
            cronograma de tranches · volume em MWm · MtM e fluxo por mês · valor em 3 lentes
          </p>
        </div>
        <span
          data-testid="sim-selo"
          className="text-[11px] px-2.5 py-1 rounded-md border border-white/10 text-white/45 bg-indra-azul-deep"
        >
          ◦ simulação · nada é salvo
        </span>
      </div>

      {/* ---- Cabeçalho comum ---- */}
      <div className="bg-indra-azul-surface border border-white/10 rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-wide text-white/50 font-medium mb-3">
          Operação (comum a todas as tranches)
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-x-4 gap-y-3">
          <Campo label="Tipo">
            <Toggle
              ariaLabel="Tipo da operação"
              opts={[
                { id: "compra", label: "Compra" },
                { id: "venda", label: "Venda" },
              ]}
              value={cab.tipo}
              onChange={(v) => setCab((c) => ({ ...c, tipo: v }))}
            />
          </Campo>
          <Campo label="Submercado">
            <select
              data-testid="sim-submercado"
              value={cab.submercado}
              onChange={(e) => setCab((c) => ({ ...c, submercado: e.target.value as Cabecalho["submercado"] }))}
              className={INPUT_CLS}
            >
              {["SE", "S", "NE", "N"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Tipo de energia">
            <select
              data-testid="sim-tipo-energia"
              value={cab.tipoEnergia}
              onChange={(e) => setCab((c) => ({ ...c, tipoEnergia: e.target.value as Cabecalho["tipoEnergia"] }))}
              className={INPUT_CLS}
            >
              {["CONV", "I0", "I5", "I100"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Indexador">
            <select
              data-testid="sim-indexador"
              value={cab.indexador}
              onChange={(e) => setCab((c) => ({ ...c, indexador: e.target.value as Indexador }))}
              className={INPUT_CLS}
            >
              {INDEXADORES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Contraparte">
            <ContrapartePicker
              nomes={cpNomes}
              carregando={cpCarregando}
              erro={cpErro}
              escolhida={cp}
              onEscolher={setCp}
            />
          </Campo>
        </div>
      </div>

      {/* ---- Tabela de tranches ---- */}
      <div className="bg-indra-azul-surface border border-white/10 rounded-lg p-4">
        <div className="text-[10px] uppercase tracking-wide text-white/50 font-medium mb-2">
          Tranches — preço, prazo e volume podem variar por linha
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-1.5 pr-2 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10 w-6">#</th>
                <th className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Período</th>
                <th className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10 w-28">MWm</th>
                <th className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10 w-36">
                  {indexado ? `Spread (R$/MWh)` : "Preço (R$/MWh)"}
                </th>
                {ehIpca && (
                  <th className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">IPCA · base / reajuste</th>
                )}
                <th className="border-b border-white/10 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {tranches.map((t, i) => (
                <tr key={t.id} data-testid={`sim-tr-row-${i}`} className="border-b border-white/5 align-top">
                  <td className="py-2 pr-2 text-sm text-white/40">{i + 1}</td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <input
                        data-testid={`sim-tr-ini-${i}`}
                        type="month"
                        value={t.mesIni}
                        onChange={(e) => setTr(t.id, { mesIni: e.target.value })}
                        className={INPUT_CLS + " !w-[9.5rem]"}
                      />
                      <span className="text-white/30 text-xs">–</span>
                      <input
                        data-testid={`sim-tr-fim-${i}`}
                        type="month"
                        value={t.mesFim}
                        onChange={(e) => setTr(t.id, { mesFim: e.target.value })}
                        className={INPUT_CLS + " !w-[9.5rem]"}
                      />
                      <button
                        type="button"
                        data-testid={`sim-tr-ano-${i}`}
                        title="preencher jan–dez do ano do mês início"
                        onClick={() => {
                          const y = /^(\d{4})/.exec(t.mesIni)?.[1];
                          if (y) setTr(t.id, { mesIni: `${y}-01`, mesFim: `${y}-12` });
                        }}
                        className="text-[11px] px-2 py-1 rounded border border-white/10 text-indra-turquesa hover:bg-white/5"
                      >
                        ano cheio
                      </button>
                    </div>
                  </td>
                  <td className="py-2 px-2">
                    <input
                      data-testid={`sim-tr-mwm-${i}`}
                      type="number"
                      min="0"
                      step="any"
                      value={t.volumeMwm}
                      onChange={(e) => setTr(t.id, { volumeMwm: e.target.value })}
                      placeholder="6,98"
                      className={INPUT_CLS}
                    />
                  </td>
                  <td className="py-2 px-2">
                    {indexado ? (
                      <input
                        data-testid={`sim-tr-spread-${i}`}
                        type="number"
                        step="any"
                        value={t.spread}
                        onChange={(e) => setTr(t.id, { spread: e.target.value })}
                        placeholder="12,50"
                        className={INPUT_CLS}
                      />
                    ) : (
                      <input
                        data-testid={`sim-tr-preco-${i}`}
                        type="number"
                        min="0"
                        step="any"
                        value={t.preco}
                        onChange={(e) => setTr(t.id, { preco: e.target.value })}
                        placeholder="185,00"
                        className={INPUT_CLS}
                      />
                    )}
                  </td>
                  {ehIpca && (
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <input
                          data-testid={`sim-tr-ipca-base-${i}`}
                          type="date"
                          value={t.ipcaBase}
                          onChange={(e) => setTr(t.id, { ipcaBase: e.target.value })}
                          className={INPUT_CLS + " !w-[10.5rem]"}
                        />
                        <input
                          data-testid={`sim-tr-ipca-reaj-${i}`}
                          type="date"
                          value={t.ipcaReaj}
                          onChange={(e) => setTr(t.id, { ipcaReaj: e.target.value })}
                          className={INPUT_CLS + " !w-[10.5rem]"}
                        />
                      </div>
                    </td>
                  )}
                  <td className="py-2 pl-2 text-right">
                    <button
                      type="button"
                      data-testid={`sim-tr-del-${i}`}
                      aria-label={`remover tranche ${i + 1}`}
                      disabled={tranches.length === 1}
                      onClick={() => setTranches((ts) => ts.filter((x) => x.id !== t.id))}
                      className={`text-sm px-1.5 ${tranches.length === 1 ? "text-white/15 cursor-not-allowed" : "text-white/40 hover:text-indra-negativo"}`}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          data-testid="sim-tr-add"
          onClick={() => setTranches((ts) => [...ts, novaTranche()])}
          className="mt-2 text-[13px] text-indra-turquesa hover:underline"
        >
          + adicionar tranche
        </button>
        <p className="text-[11px] text-white/40 mt-2" data-testid="sim-tr-rodape">
          Total: {totalMesesForm} {totalMesesForm === 1 ? "mês" : "meses"}
          {resultado && (
            <>
              {" "}· {resultado.operacao.volume_mwh_total.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} MWh
              {/* F-B2: caixa da operação = NET entradas+saídas (venda→entradas,
                  compra→saídas) — sinal do MtM não decide lado de caixa. */}
              · nominal {fmtBRLcompact(resultado.fluxo.total_entradas + resultado.fluxo.total_saidas)}
            </>
          )}
          {" "}· uma baseline só
        </p>

        {faltando.length > 0 && (
          <div data-testid="sim-pendencias" className="mt-3 text-[12px] text-white/50">
            <span className="text-white/70 font-semibold">Para simular, falta:</span>{" "}
            {faltando.join(" · ")}
          </div>
        )}
        {faltando.length === 0 && calculando && (
          <div data-testid="sim-calculando" className="mt-3 text-[12px] text-indra-turquesa">
            ⟳ recalculando…
          </div>
        )}
        {erro && (
          <div data-testid="sim-erro" className="mt-3 text-[12px] text-indra-negativo">
            Falha ao simular: {erro}
          </div>
        )}
      </div>

      {/* ---- Toggle de valor + 3 cards ---- */}
      {resultado && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-wide text-white/50 font-medium">Valor em</span>
            <Toggle ariaLabel="Lente de valor" opts={lenteOpts} value={lente} onChange={setLente} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {/* 1 · MtM / PnL (na lente) */}
            <DeltaCard titulo="MtM / PnL" asOf={resultado.mtm.baseline_data_ref} testid="sim-card-mtm">
              <div className="flex items-baseline gap-2 flex-wrap font-mono">
                <span className="text-white/60 text-sm">{fmtBRLcompact(resultado.mtm.baseline_total_vivo)}</span>
                <span className="text-white/30 text-xs">→</span>
                <span className="text-white text-lg font-bold" data-testid="sim-mtm-depois">
                  {mtmTotalLente === null
                    ? "—"
                    : fmtBRLcompact(resultado.mtm.baseline_total_vivo + mtmTotalLente)}
                </span>
                <span className="text-xs text-indra-turquesa" data-testid="sim-mtm-delta">
                  ({mtmTotalLente === null ? "—" : fmtBRLcompactSigned(mtmTotalLente)})
                </span>
                <span className="text-[11px] text-white/40 font-sans">{LENTE_SUB[lente]}</span>
              </div>
              {mtmTotalLente === null && lente !== "nominal" && (
                <p className="text-[11px] text-amber-400/80" data-testid="sim-mtm-nsem">
                  total indisponível na lente: {mtmNSem} {mtmNSem === 1 ? "mês" : "meses"} sem{" "}
                  {lente === "vp" ? "curva p/ VP" : "implícita"} — nunca soma parcial muda.
                </p>
              )}
              {resultado.mtm.n_sem_marcacao > 0 && (
                <p className="text-[11px] text-amber-400/80">
                  {resultado.mtm.n_sem_marcacao} {resultado.mtm.n_sem_marcacao === 1 ? "mês" : "meses"} sem
                  marcação (fora da curva) — não somam R$ 0 mudo.
                </p>
              )}
              <div className="max-h-64 overflow-y-auto -mx-1 px-1">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Mês</th>
                      <th className="text-right py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">MtM</th>
                    </tr>
                  </thead>
                  <tbody data-testid="sim-mtm-tabela">
                    {resultado.mtm.por_mes.map((m) => {
                      const v = mtmDe(m);
                      const motivo = m.motivo_sem_marcacao ?? motivoLenteMtm(m);
                      return (
                        <tr key={m.mes} className="border-b border-white/5">
                          <td className="py-1 text-xs text-white/70 font-mono">{m.mes}</td>
                          <td className="py-1 text-right font-mono text-xs whitespace-nowrap">
                            {v === null ? (
                              <span className="text-white/35 italic">
                                {m.motivo_sem_marcacao ? "sem marcação" : "sem lente"} · {motivo}
                              </span>
                            ) : (
                              <span className={corValor(v)}>{fmtBRL(v)}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </DeltaCard>

            {/* 2 · Fluxo (na lente) */}
            <DeltaCard titulo="Fluxo de caixa · pernas nos 6DUs" testid="sim-card-fluxo">
              {lente === "nominal" ? (
                <div className="flex gap-5 font-mono text-sm">
                  <span>
                    <span className="text-white/40 text-[10px] uppercase block">Entradas</span>
                    <b className="text-indra-positivo">{fmtBRLcompact(resultado.fluxo.total_entradas)}</b>
                  </span>
                  <span>
                    <span className="text-white/40 text-[10px] uppercase block">Saídas</span>
                    <b className="text-indra-negativo">{fmtBRLcompact(resultado.fluxo.total_saidas)}</b>
                  </span>
                </div>
              ) : (
                <div className="font-mono text-sm">
                  <span className="text-white/40 text-[10px] uppercase block">
                    Total (net) {LENTE_SUB[lente]}
                  </span>
                  <b data-testid="sim-fluxo-total-lente" className={fluxoTotalLente === null ? "text-white/40" : corValor(fluxoTotalLente)}>
                    {fluxoTotalLente === null ? "— (cobertura parcial)" : fmtBRLcompact(fluxoTotalLente)}
                  </b>
                </div>
              )}
              <PernasTimeline pernas={resultado.fluxo.pernas} lente={lente} />
              <div className="max-h-56 overflow-y-auto -mx-1 px-1">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Forn.</th>
                      <th className="text-left py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Pagto (6DU)</th>
                      <th className="text-right py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Valor</th>
                    </tr>
                  </thead>
                  <tbody data-testid="sim-fluxo-tabela">
                    {resultado.fluxo.pernas.map((p) => {
                      const v = pernaDe(p);
                      const motivoL = motivoLentePerna(p);
                      return (
                        <tr key={p.mes_fornecimento} className="border-b border-white/5">
                          <td className="py-1 text-xs text-white/70 font-mono">{p.mes_fornecimento}</td>
                          <td className="py-1 text-xs text-white/70 font-mono">
                            {p.data ? brDate(p.data) : "—"}
                            <span className="text-white/30 ml-1">· {p.fonte_data}</span>
                          </td>
                          <td className="py-1 text-right font-mono text-xs whitespace-nowrap">
                            {p.valor === null ? (
                              <span className="text-white/35 italic">sem curva p/ estimar</span>
                            ) : v === null ? (
                              <span className="text-white/35 italic">sem lente · {motivoL}</span>
                            ) : (
                              <>
                                <span className={corValor(v)}>{fmtBRL(v)}</span>
                                {p.valor_estimado && (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-amber-400/12 text-amber-400 ml-1 font-semibold align-middle">
                                    estimado
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-white/35 leading-relaxed">
                {resultado.fluxo.sinal}. Indexado futuro é <b>estimativa</b> (forward + spread).{" "}
                {lente !== "nominal" && "Lente re-expressa cada perna; buraco de cobertura fica explícito."}
              </p>
            </DeltaCard>

            {/* 3 · Exposição (lente NÃO se aplica — nominal com nota) */}
            <DeltaCard
              titulo={`Exposição · ${resultado.exposicao.contraparte}`}
              asOf={resultado.exposicao.baseline_data_ref}
              testid="sim-card-exposicao"
            >
              {!resultado.exposicao.existe_baseline && (
                <p className="text-[11px] text-amber-400/80">contraparte nova — sem posição no baseline.</p>
              )}
              <div className="flex items-baseline gap-2 flex-wrap font-mono">
                <span className="text-white/60 text-sm">{fmtBRLcompact(resultado.exposicao.antes.mtm_liquido)}</span>
                <span className="text-white/30 text-xs">→</span>
                <span className="text-white text-lg font-bold">{fmtBRLcompact(resultado.exposicao.depois.mtm_liquido)}</span>
                <span className="text-xs text-indra-turquesa">({fmtBRLcompactSigned(resultado.exposicao.delta.mtm)})</span>
              </div>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10"></th>
                    <th className="text-right py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Antes</th>
                    <th className="text-right py-1.5 text-[10px] uppercase tracking-wide text-white/50 border-b border-white/10">Depois</th>
                  </tr>
                </thead>
                <tbody className="text-xs font-mono">
                  <tr className="border-b border-white/5">
                    <td className="py-1.5 font-sans text-white/60">MtM líquido</td>
                    <td className={"py-1.5 text-right " + corValor(resultado.exposicao.antes.mtm_liquido)}>
                      {fmtBRL(resultado.exposicao.antes.mtm_liquido)}
                    </td>
                    <td className={"py-1.5 text-right " + corValor(resultado.exposicao.depois.mtm_liquido)}>
                      {fmtBRL(resultado.exposicao.depois.mtm_liquido)}
                    </td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-1.5 font-sans text-white/60">Posição líq. (MWm)</td>
                    <td className="py-1.5 text-right text-white/70">{resultado.exposicao.antes.posicao_liquida_mwm.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                    <td className="py-1.5 text-right text-white">{resultado.exposicao.depois.posicao_liquida_mwm.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-1.5 font-sans text-white/60">Direção</td>
                    <td className="py-1.5 text-right"><DirecaoBadge direcao={resultado.exposicao.antes.direcao} /></td>
                    <td className="py-1.5 text-right"><DirecaoBadge direcao={resultado.exposicao.depois.direcao} /></td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-1.5 font-sans text-white/60">Σ net compradas</td>
                    <td className="py-1.5 text-right text-white/70">{fmtBRLcompact(resultado.exposicao.split_antes.compradas)}</td>
                    <td className="py-1.5 text-right text-white">{fmtBRLcompact(resultado.exposicao.split_depois.compradas)}</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-1.5 font-sans text-white/60">Σ net vendidas</td>
                    <td className="py-1.5 text-right text-white/70">{fmtBRLcompact(resultado.exposicao.split_antes.vendidas)}</td>
                    <td className="py-1.5 text-right text-white">{fmtBRLcompact(resultado.exposicao.split_depois.vendidas)}</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 font-sans text-white/60">Limite de crédito</td>
                    <td colSpan={2} className="py-1.5 text-right text-white/30 italic font-sans" data-testid="sim-limite-gated">
                      — configurar · em breve
                    </td>
                  </tr>
                </tbody>
              </table>
              <p className="text-[10px] text-white/35 leading-relaxed" data-testid="sim-exp-nota-lente">
                Sempre nominal — a lente re-expressa dinheiro no tempo; posição e limite não são caixa.{" "}
                {resultado.exposicao.nota_mwm}
              </p>
            </DeltaCard>
          </div>
        </>
      )}
    </div>
  );
}
